import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Clock, Hammer, Mail, ArrowRight } from 'lucide-react';
import type { ComingSoonContent, MaintenanceContent } from '@meshsmith/shared';
import { MeshMark, MeshWordmark } from '../components/Logo';
import { Seo } from '../components/Seo';
import { api } from '../lib/api';
import { useToast } from '../hooks/useToast';

function useCountdown(target: string | null) {
  const targetMs = useMemo(() => (target ? Date.parse(target) : null), [target]);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!targetMs) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [targetMs]);
  if (!targetMs) return null;
  const diff = Math.max(0, targetMs - now);
  return {
    done: diff === 0,
    days: Math.floor(diff / 86_400_000),
    hours: Math.floor((diff % 86_400_000) / 3_600_000),
    minutes: Math.floor((diff % 3_600_000) / 60_000),
    seconds: Math.floor((diff % 60_000) / 1000),
  };
}

function Shell({
  children,
  seoTitle,
}: {
  children: ReactNode;
  seoTitle: string;
}) {
  return (
    <div className="relative flex min-h-screen flex-col overflow-hidden bg-warm-off-white">
      <Seo title={seoTitle} noindex description="MeshSmith - From Mesh to Matter" />

      {/* soft brand glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-[-10%] h-[520px] w-[520px] -translate-x-1/2 rounded-full opacity-60 blur-3xl"
        style={{ background: 'radial-gradient(closest-side, var(--pale-aqua), transparent)' }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute bottom-[-15%] right-[-10%] h-[420px] w-[420px] rounded-full opacity-40 blur-3xl"
        style={{ background: 'radial-gradient(closest-side, var(--brand-tint), transparent)' }}
      />
      <div className="absolute left-0 top-0 h-1 w-full bg-brand-gradient" />

      <main className="relative z-10 flex flex-1 items-center justify-center px-5 py-14">
        <div className="w-full max-w-xl text-center">{children}</div>
      </main>

      <footer className="relative z-10 pb-8 text-center text-xs text-secondary-grey">
        © {new Date().getFullYear()} MeshSmith · From Mesh to Matter
      </footer>
    </div>
  );
}

function BrandLockup() {
  return (
    <div className="flex flex-col items-center">
      <MeshMark className="h-16 w-16 drop-shadow-sm" />
      <MeshWordmark className="mt-3 block text-2xl" />
      <span className="mt-1 block text-[0.6rem] font-semibold uppercase tracking-[0.32em] text-secondary-grey">
        From Mesh to Matter
      </span>
    </div>
  );
}

function Pill({ icon: Icon, children }: { icon: typeof Clock; children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-warm-orange/30 bg-brand-tint px-3.5 py-1 text-[0.72rem] font-bold uppercase tracking-[0.15em] text-logo-to">
      <Icon size={12} />
      {children}
    </span>
  );
}

function Countdown({ target }: { target: string | null }) {
  const c = useCountdown(target);
  if (!c) return null;
  const cells: [number, string][] = [
    [c.days, 'days'],
    [c.hours, 'hrs'],
    [c.minutes, 'min'],
    [c.seconds, 'sec'],
  ];
  return (
    <div className="mx-auto mt-8 flex max-w-sm justify-center gap-2.5">
      {cells.map(([n, label]) => (
        <div
          key={label}
          className="flex-1 rounded-xl border bg-white/80 px-2 py-3 backdrop-blur"
          style={{ borderColor: 'var(--border)' }}
        >
          <div className="text-2xl font-bold tabular-nums leading-none text-charcoal">
            {String(n).padStart(2, '0')}
          </div>
          <div className="mt-1 text-[0.6rem] font-semibold uppercase tracking-wide text-secondary-grey">
            {label}
          </div>
        </div>
      ))}
    </div>
  );
}

export function ComingSoonScreen({ content }: { content: ComingSoonContent }) {
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const subscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/content/newsletter', { email, source: 'coming-soon' });
      setDone(true);
      toast.push('You are on the list', 'success');
    } catch {
      toast.push('Could not sign you up right now', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell seoTitle={`${content.headline} · MeshSmith`}>
      <BrandLockup />

      <div className="mt-8">
        <Pill icon={Hammer}>Coming soon</Pill>
      </div>

      <h1 className="mt-5 text-[2rem] font-bold leading-tight text-charcoal sm:text-4xl">
        {content.headline}
      </h1>
      {content.message && (
        <p className="mx-auto mt-3 max-w-md text-secondary-grey">{content.message}</p>
      )}

      {content.showCountdown && content.launchAt && <Countdown target={content.launchAt} />}

      {content.showNewsletter && (
        <div className="mx-auto mt-9 max-w-sm">
          {done ? (
            <p className="rounded-xl border border-deep-teal/25 bg-pale-aqua px-4 py-3 text-sm font-medium text-deep-teal">
              Thanks — we’ll email you the moment the studio opens.
            </p>
          ) : (
            <form onSubmit={subscribe}>
              <div
                className="flex items-center gap-1 rounded-full border bg-white p-1 shadow-sm focus-within:border-deep-teal"
                style={{ borderColor: 'var(--border)' }}
              >
                <Mail size={16} className="ml-3 shrink-0 text-secondary-grey" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full bg-transparent px-2 py-2 text-sm outline-none"
                  aria-label="Email address"
                />
                <button
                  className="btn btn-accent shrink-0 rounded-full px-4 py-2 text-sm"
                  disabled={busy}
                  aria-label="Notify me"
                >
                  Notify me <ArrowRight size={15} />
                </button>
              </div>
              <p className="mt-2 text-[0.7rem] text-secondary-grey">No spam — one email when we launch.</p>
            </form>
          )}
        </div>
      )}
    </Shell>
  );
}

export function MaintenanceScreen({ content }: { content: MaintenanceContent }) {
  return (
    <Shell seoTitle={`${content.headline} · MeshSmith`}>
      <BrandLockup />

      <div className="mt-8">
        <Pill icon={Clock}>Maintenance</Pill>
      </div>

      <h1 className="mt-5 text-[2rem] font-bold leading-tight text-charcoal sm:text-4xl">
        {content.headline}
      </h1>
      {content.message && (
        <p className="mx-auto mt-3 max-w-md text-secondary-grey">{content.message}</p>
      )}

      {content.estimatedBackAt && (
        <div
          className="mx-auto mt-8 inline-flex items-center gap-2.5 rounded-xl border bg-white/80 px-4 py-3 text-sm backdrop-blur"
          style={{ borderColor: 'var(--border)' }}
        >
          <Clock size={16} className="text-deep-teal" />
          <span className="text-secondary-grey">
            Expected back around{' '}
            <strong className="text-charcoal">
              {new Date(content.estimatedBackAt).toLocaleString('en-IN', {
                dateStyle: 'medium',
                timeStyle: 'short',
              })}
            </strong>
          </span>
        </div>
      )}
    </Shell>
  );
}
